import {Injectable, signal} from '@angular/core';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  text: string;
  duration: number;
  type: ToastType;
}

/**
 * Servizio per la gestione dei messaggi toast floating.
 * Espone un signal `current` con il messaggio attivo (null se nessuno)
 * e `isLeaving` per triggerare l'animazione di uscita prima della rimozione dal DOM.
 */
@Injectable({providedIn: 'root'})
export class ToastService {

  readonly current = signal<ToastMessage | null>(null);
  readonly isLeaving = signal<boolean>(false);

  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _leaveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Durata animazione di uscita in ms — deve corrispondere alla CSS animation */
  private readonly LEAVE_ANIMATION_MS = 400;

  show(text: string, type: ToastType = 'info', duration = 3000): void {
    // Annulla eventuali timer pendenti
    this._clearTimers();
    // Forza reset isLeaving se c'era un toast precedente
    this.isLeaving.set(false);
    this.current.set({text, duration, type});

    // Dopo `duration` ms avvia l'animazione di uscita
    this._timer = setTimeout(() => {
      this._startLeave();
    }, duration);
  }

  dismiss(): void {
    this._clearTimers();
    this._startLeave();
  }

  private _startLeave(): void {
    this.isLeaving.set(true);
    // Rimuove il toast dal DOM dopo che l'animazione di uscita è completata
    this._leaveTimer = setTimeout(() => {
      this.current.set(null);
      this.isLeaving.set(false);
    }, this.LEAVE_ANIMATION_MS);
  }

  private _clearTimers(): void {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    if (this._leaveTimer) {
      clearTimeout(this._leaveTimer);
      this._leaveTimer = null;
    }
  }
}
